// MainWebsite.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './App.css';
import AuthModal from './AuthModal';
import { hasSupabaseConfig, supabase } from './supabaseClient';
const lockImage = require('./lock.png');
const isAbortError = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError') return true;
  if (typeof error.message === 'string' && error.message.toLowerCase().includes('aborted')) return true;
  return false;
};

function MainWebsite() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const APK_DOWNLOAD_LINK = 'https://drive.google.com/file/d/1DByWFvkEJxIQGvd0E1EDxRONcxg0iWFV/view?usp=drive_link';

  const features = [
    {
      icon: 'fas fa-money-bill-wave',
      title: 'Earn Money Chatting',
      description: 'Set your own rate and get paid for 24-hour chat sessions with your fans.'
    },
    {
      icon: 'fas fa-users',
      title: 'Connect with Fans',
      description: 'Build deeper connections with your most dedicated followers.'
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'Secure Payments',
      description: 'Get paid instantly with 13% platform fee deducted automatically.'
    },
    {
      icon: 'fas fa-clock',
      title: 'Flexible Schedule',
      description: 'Chat on your own time - you control when and how much you engage.'
    }
  ];

  const steps = [
    {
      number: 1,
      title: 'Sign Up as Celebrity',
      description: 'Create your profile and verify your account in minutes'
    },
    {
      number: 2,
      title: 'Set Your Chat Rate',
      description: 'Choose how much fans pay for 24-hour access (100-1500 EGP)'
    },
    {
      number: 3,
      title: 'Get Discovery',
      description: 'Fans find you through search and can unlock chats'
    },
    {
      number: 4,
      title: 'Earn & Chat',
      description: 'Get paid instantly when fans unlock chats with you'
    }
  ];

  const earnings = [
    { rate: '100 EGP', amount: '87 EGP' },
    { rate: '200 EGP', amount: '174 EGP' },
    { rate: '325 EGP', amount: '283 EGP' },
    { rate: '500 EGP', amount: '435 EGP' },
    { rate: '700 EGP', amount: '609 EGP' },
    { rate: '1000 EGP', amount: '870 EGP' },
    { rate: '1500 EGP', amount: '1305 EGP' }
  ];

  const perfectForItems = [
    'Influencers & Content Creators',
    'Actors & Musicians',
    'Athletes & Public Figures',
    'Experts & Thought Leaders',
    'Anyone with a dedicated fanbase in Egypt'
  ];

  const handleDownloadClick = (e) => {
    e.preventDefault();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setDownloading(false);
    setDownloadComplete(false);
  };

  const handleDownload = () => {
    setDownloading(true);
    
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = APK_DOWNLOAD_LINK;
      link.download = 'Unlock-App.apk';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setDownloading(false);
      setDownloadComplete(true);
    }, 2000);
  };

  const handleAuthSuccess = (profile) => {
    if (profile) {
      navigate('/app', { replace: true });
    }
    setShowAuthModal(false);
  };

  useEffect(() => {
    if (!hasSupabaseConfig) return undefined;

    let mounted = true;

    const initAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!mounted) return;

        if (session?.user) {
          navigate('/app', { replace: true });
        }
      } catch (error) {
        if (!mounted || isAbortError(error)) return;
        // eslint-disable-next-line no-console
        console.error('Auth check failed on landing page:', error);
      }
    };

    void initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;

      if (session?.user) {
        navigate('/app', { replace: true });
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  useEffect(() => {
    document.title = 'Unlock | Turn Your Influence Into Income';

    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute(
        'content',
        'Unlock helps creators and influencers in Egypt monetize direct fan conversations through paid 24-hour chat sessions.'
      );
    }

    const robotsTag = document.querySelector('meta[name="robots"]');
    if (robotsTag) {
      robotsTag.setAttribute('content', 'index, follow');
    }

    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute('href', 'https://unlockapp.me/');
    }
  }, []);

  useEffect(() => {
    // Ensure Font Awesome loads
    if (!document.querySelector('link[href*="font-awesome"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
      document.head.appendChild(link);
    }

    const featureCards = document.querySelectorAll('.feature-card');
    const stepCards = document.querySelectorAll('.step-card');
    
    featureCards.forEach(card => {
      card.style.opacity = '0.9';
      card.style.transform = 'translateY(20px)';
    });
    
    stepCards.forEach(card => {
      card.style.opacity = '0.9';
      card.style.transform = 'translateX(-20px)';
    });

    const handleScroll = () => {
      featureCards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          card.style.opacity = '1';
          card.style.transform = 'translateY(0)';
          card.style.transition = `all 0.5s ease ${index * 0.1}s`;
        }
      });
      
      stepCards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          card.style.opacity = '1';
          card.style.transform = 'translateX(0)';
          card.style.transition = `all 0.5s ease ${index * 0.1}s`;
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const DownloadModal = () => {
    if (!showModal) return null;

    if (downloadComplete) {
      return (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ color: '#3266a8', fontSize: '48px', marginBottom: '20px' }}>
              <i className="fas fa-check-circle"></i>
            </div>
            <h3 style={{ color: '#fff', marginBottom: '15px', fontSize: '24px' }}>Download Started!</h3>
            <p style={{ color: '#ccc', marginBottom: '20px', lineHeight: '1.6' }}>
              Please check your downloads folder for <strong>Unlock-App.apk</strong>
            </p>
            <p style={{ color: '#888', fontSize: '14px', marginBottom: '30px' }}>
              <i className="fas fa-info-circle"></i> If download doesn't start automatically, check your browser's download bar.
            </p>
            <div style={{ background: 'rgba(50, 102, 168, 0.1)', padding: '15px', borderRadius: '8px', border: '1px solid rgba(50, 102, 168, 0.2)', marginBottom: '30px' }}>
              <p style={{ color: '#3266a8', margin: '0', fontSize: '14px' }}>
                <i className="fas fa-shield-alt"></i> This APK is safe to install. You might need to allow "Install from Unknown Sources" in your Android settings.
              </p>
            </div>
            <button 
              onClick={closeModal}
              style={{
                background: '#3266a8',
                color: 'white',
                border: 'none',
                padding: '12px 32px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Got It!
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="modal-overlay" onClick={closeModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div style={{ color: '#3266a8', fontSize: '48px', marginBottom: '20px' }}>
            <i className="fas fa-download"></i>
          </div>
          <h3 style={{ color: '#fff', marginBottom: '15px', fontSize: '24px' }}>Download Unlock APK</h3>
          <p style={{ color: '#ccc', marginBottom: '30px', lineHeight: '1.6' }}>
            Ready to start earning? The download will begin shortly.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              onClick={handleDownload}
              disabled={downloading}
              style={{
                background: downloading ? '#2a5690' : '#3266a8',
                color: 'white',
                border: 'none',
                padding: '12px 32px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: downloading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {downloading ? (
                <>
                  <div className="loading"></div>
                  Downloading...
                </>
              ) : (
                'Download Now (APK)'
              )}
            </button>
            <button 
              onClick={closeModal}
              style={{
                background: 'transparent',
                color: '#ccc',
                border: '1px solid #1a1a1a',
                padding: '12px 32px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Cancel
            </button>
          </div>
          <p style={{ color: '#888', fontSize: '14px', marginTop: '20px' }}>
            File: unlock-app.apk | Size: ~100MB
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="container header-content">
          <div className="logo-container">
            <div className="logo-img-wrapper">
              <img src={lockImage} alt="Unlock Logo" className="logo-img" onError={(e) => {
                e.target.style.display = 'none';
                const fallbackIcon = document.createElement('i');
                fallbackIcon.className = 'fas fa-lock';
                fallbackIcon.style.cssText = 'color: #3266a8; font-size: 24px;';
                e.target.parentNode.appendChild(fallbackIcon);
              }} />
            </div>
            <div className="logo-text">Unlock</div>
          </div>

          <div className="auth-header-actions">
            <button
              type="button"
              className="auth-open-btn"
              onClick={() => setShowAuthModal(true)}
              disabled={!hasSupabaseConfig}
            >
              {hasSupabaseConfig ? 'Login / Sign Up' : 'Auth Unavailable'}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="egypt-badge">
            <i className="fas fa-location-dot"></i>
            <span>Available in Egypt | Payments in EGP</span>
          </div>
          
          <h1>Earn Money <span>Chatting</span></h1>
          <p>Turn your influence into income. Get paid for 24-hour chat sessions with your fans.</p>
          
          <div className="hero-subtitle">
            Use the web app instantly or download Android APK if you prefer.
          </div>

          <div className="hero-cta-group">
            <button
              type="button"
              className="download-btn glow web-cta-btn"
              onClick={() => setShowAuthModal(true)}
              disabled={!hasSupabaseConfig}
            >
              <i className="fas fa-right-to-bracket"></i>
              {hasSupabaseConfig ? 'Use Web App' : 'Auth Unavailable'}
            </button>
            <button
              type="button"
              className="download-btn secondary-download-btn"
              onClick={handleDownloadClick}
            >
              <i className="fab fa-android"></i>
              Download APK (Optional)
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="container">
        <div className="stats">
          <div className="stat-item">
            <div className="stat-number">87%</div>
            <div className="stat-label">You Keep</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">24hr</div>
            <div className="stat-label">Chat Duration</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">1500 EGP</div>
            <div className="stat-label">Max Rate</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Why Join <span>Unlock?</span></h2>
          <p className="section-subtitle">Monetize your influence directly with your fans through secure, flexible chat sessions</p>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div className="feature-card" key={index}>
                <div className="feature-icon">
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section" style={{ background: '#0d0d0d' }}>
        <div className="container">
          <h2 className="section-title">How You <span>Earn</span></h2>
          <p className="section-subtitle">Start making money in 4 simple steps</p>
          
          <div className="steps-container">
            {steps.map((step) => (
              <div className="step-card" key={step.number}>
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Calculator Section */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Your Earning <span>Potential</span></h2>
          <p className="section-subtitle">You keep 87% of every chat (13% platform fee)</p>
          
          <div className="earnings-card">
            <div className="earnings-header">
              <div>Chat Rate (EGP)</div>
              <div>You Earn (EGP)</div>
            </div>
            
            {earnings.map((item, index) => (
              <div className="earnings-row" key={index}>
                <div className="rate">{item.rate}</div>
                <div className="earnings-amount">{item.amount}</div>
              </div>
            ))}
            
            <div className="earnings-note">
              <i className="fas fa-calculator"></i>
              Example: At 325 EGP per chat, you earn 283 EGP. 5 chats = 1,415 EGP
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Perfect <span>For</span></h2>
          
          <div className="perfect-for">
            {perfectForItems.map((item, index) => (
              <div className="perfect-for-item" key={index}>
                <i className="fas fa-check-circle"></i>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="section download-section">
        <div className="container">
          <div className="download-card">
            <h2>Start Earning <span>Today</span></h2>
            <p>Create your account and start on web right now. Android APK download is optional.</p>

            <div className="download-actions">
              <button
                type="button"
                className="download-btn glow web-cta-btn"
                onClick={() => setShowAuthModal(true)}
                disabled={!hasSupabaseConfig}
              >
                <i className="fas fa-right-to-bracket"></i>
                {hasSupabaseConfig ? 'Login / Sign Up on Web' : 'Auth Unavailable'}
              </button>
              <button
                type="button"
                className="download-btn secondary-download-btn"
                onClick={handleDownloadClick}
              >
                <i className="fab fa-android"></i>
                Download Unlock APK
              </button>
            </div>

            <div className="apk-info">Web app available now | APK available in Egypt</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <div className="footer-logo-img-wrapper">
                <img src={lockImage} alt="Unlock Logo" className="footer-logo-img" onError={(e) => {
                  e.target.style.display = 'none';
                  const fallbackIcon = document.createElement('i');
                  fallbackIcon.className = 'fas fa-lock';
                  fallbackIcon.style.cssText = 'color: #ffffff; font-size: 20px;';
                  e.target.parentNode.appendChild(fallbackIcon);
                }} />
              </div>
              <div className="footer-logo-text">Unlock</div>
            </div>
            <p className="footer-tagline">Turning Influence into Income | Egypt</p>
            
            <div className="footer-links">
              <a href="mailto:support@unlockapp.me">Contact Us</a>
              <a href="mailto:support@unlockapp.me">Support</a>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
            </div>
            
            <div className="copyright">(c) 2026 Unlock App. Currently available only in Egypt. All payments in EGP.<br />
              Connect with your fans and monetize your influence securely.
            </div>
          </div>
        </div>
      </footer>

      {/* Download Modal */}
      <DownloadModal />
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={handleAuthSuccess}
      />
    </div>
  );
}

export default MainWebsite;


